import {
  SPOTLIGHT_EMPTY_ACTION_LIMIT,
  type SpotlightActionDef,
  type SpotlightActionKind,
} from './actionCatalog';
import {
  acronymFromPhrase,
  isAcronymQuery,
} from './acronymMatch';
import { normalizeQuery } from './normalizeQuery';

export type FilterSpotlightActionsOptions = {
  query: string;
  /** Resolved label text for matching (already translated). */
  getLabel: (action: SpotlightActionDef) => string;
  hasPermission: (permission: string) => boolean;
  emptyLimit?: number;
};

/** Phrase / substring match (non-acronym queries). */
export const SCORE_PHRASE = 100;
/** Exact word or word-prefix on acronym-shaped queries. */
export const SCORE_WORD_PREFIX = 80;
/** Acronym equals query (e.g. tlh → tlh). */
export const SCORE_ACRONYM_EXACT = 60;
/** Acronym starts with query (e.g. tl → tlh). */
export const SCORE_ACRONYM_PREFIX = 40;

const KIND_ORDER: Record<SpotlightActionKind, number> = {
  create: 0,
  command: 1,
  navigate: 2,
};

const haystackFor = (
  action: SpotlightActionDef,
  label: string,
): string =>
  normalizeQuery([label, ...action.keywords].join(' '));

const phrasesFor = (
  action: SpotlightActionDef,
  label: string,
): string[] => [label, ...action.keywords];

const wordsFor = (haystack: string): string[] =>
  haystack.split(/\s+/).filter(Boolean);

/** Exact word or word-prefix — avoids `entitlements`.includes(`tl`). */
const matchesWordPrefix = (token: string, words: string[]): boolean =>
  words.some((word) => word === token || word.startsWith(token));

const isAllowed = (
  action: SpotlightActionDef,
  hasPermission: (permission: string) => boolean,
): boolean => {
  if (action.permissionsAny && action.permissionsAny.length > 0) {
    return action.permissionsAny.some((p) => hasPermission(p));
  }
  if (!action.permission) return true;
  return hasPermission(action.permission);
};

/** Best acronym score across phrases, or null if none match. */
const bestAcronymScore = (
  normalizedQuery: string,
  phrases: string[],
): number | null => {
  let best: number | null = null;
  for (const phrase of phrases) {
    const acronym = acronymFromPhrase(phrase);
    if (acronym.length < 2) continue;
    if (acronym === normalizedQuery) {
      best = Math.max(best ?? 0, SCORE_ACRONYM_EXACT);
    } else if (acronym.startsWith(normalizedQuery)) {
      best = Math.max(best ?? 0, SCORE_ACRONYM_PREFIX);
    }
  }
  return best;
};

/**
 * Match score for ranking, or null when the action does not match.
 * Higher is better: phrase (100) > word-prefix (80) > acronym exact (60) > prefix (40).
 */
export const scoreSpotlightAction = (
  action: SpotlightActionDef,
  normalizedQuery: string,
  label: string,
): number | null => {
  if (!normalizedQuery) return null;

  const tokens = normalizedQuery.split(/\s+/).filter(Boolean);
  const haystack = haystackFor(action, label);
  const phrases = phrasesFor(action, label);

  if (isAcronymQuery(normalizedQuery)) {
    const words = wordsFor(haystack);
    if (tokens.every((token) => matchesWordPrefix(token, words))) {
      return SCORE_WORD_PREFIX;
    }
    return bestAcronymScore(normalizedQuery, phrases);
  }

  if (tokens.every((token) => haystack.includes(token))) {
    return SCORE_PHRASE;
  }
  return null;
};

/**
 * Permission-filter + query match + score ranking.
 * Empty query → create first, then non-nav suggestions (respect hideWhenEmpty).
 * Contiguous letter queries match acronyms and word prefixes (not mid-word substrings).
 */
export const filterSpotlightActions = (
  actions: SpotlightActionDef[],
  options: FilterSpotlightActionsOptions,
): SpotlightActionDef[] => {
  const {
    query,
    getLabel,
    hasPermission,
    emptyLimit = SPOTLIGHT_EMPTY_ACTION_LIMIT,
  } = options;

  const allowed = actions.filter((action) => isAllowed(action, hasPermission));

  const normalized = normalizeQuery(query);
  if (!normalized) {
    const creates = allowed.filter((a) => a.kind === 'create');
    const rest = allowed.filter(
      (a) => a.kind !== 'create' && !a.hideWhenEmpty,
    );
    return [...creates, ...rest].slice(0, emptyLimit);
  }

  const ranked = allowed
    .map((action, catalogIndex) => {
      const score = scoreSpotlightAction(
        action,
        normalized,
        getLabel(action),
      );
      return score === null ? null : { action, score, catalogIndex };
    })
    .filter(
      (row): row is { action: SpotlightActionDef; score: number; catalogIndex: number } =>
        row !== null,
    );

  ranked.sort((a, b) => {
    if (b.score !== a.score) return b.score - a.score;
    const kindDiff = KIND_ORDER[a.action.kind] - KIND_ORDER[b.action.kind];
    if (kindDiff !== 0) return kindDiff;
    return a.catalogIndex - b.catalogIndex;
  });

  return ranked.map((row) => row.action);
};

/** Split filtered actions into primary (create/command) vs go/nav for UI sections. */
export const partitionSpotlightActions = (
  actions: SpotlightActionDef[],
): { primary: SpotlightActionDef[]; go: SpotlightActionDef[] } => {
  const primary = actions.filter(
    (a) => a.kind === 'create' || a.kind === 'command',
  );
  const go = actions.filter((a) => a.kind === 'navigate');
  return { primary, go };
};
